import React from 'react'
import * as yup from 'yup'

import {
  addressLengthMessage,
  cityLengthMessage,
  cellPhoneMessage,
  firstNameLengthMessage,
  firstNameMessage,
  homePhoneMessage,
  lastNameLengthMessage,
  lastNameMessage,
  middleNameLengthMessage,
  personalEmailMessage,
  personalEmailLengthMessage,
  workEmailLengthMessage,
  workEmailMessage,
  workPhoneMessage,
  zipMessage,
  zipLengthMessage
} from '../../../../strings/messages'
import { alphaRegex, phoneRegex, zipRegex } from '../../../../strings/regex'
import {
  addressLabel,
  address2Label,
  cellPhoneLabel,
  firstNameLabel,
  homeAddressLabel,
  homePhoneLabel,
  middleNameLabel,
  lastNameLabel,
  optionalLabel,
  personalEmailLabel,
  suffixLabel,
  workEmailLabel,
  workPhoneLabel
} from '../../../../strings/labels'
import { ContactDetailsName, ContactDetailsInfo } from './complainantDetails'
import { Divider } from '../../../../components/divider'
import { suffixes } from '../../../../choices'
import { validContact } from '../../../../utilities/methods/validContact'

const HomeAddressHeading = () => (
  <h2 className="ds-u-margin-bottom--0">{homeAddressLabel}</h2>
)

export const complainantDetailsPage = () => ({
  id: 'complainant_details_page',
  title: 'Your details',
  description: `We need some details about you in order for us to start an investigation.
      Please provide as much detail as you can.`,
  validation: yup.object().shape({
    complainant_first_name: yup
      .string()
      .matches(alphaRegex, {
        message: firstNameMessage,
        excludeEmptyString: true
      })
      .max(20, firstNameLengthMessage)
      .test('anon_ack_fn', firstNameMessage, function(value) {
        const { anonymous_acknowledgement } = this.parent
        if (anonymous_acknowledgement && anonymous_acknowledgement[0] === 'Yes')
          return true
        return value != null
      }),
    complainant_middle_name: yup.string().max(20, middleNameLengthMessage),
    complainant_last_name: yup
      .string()
      .matches(alphaRegex, {
        message: lastNameMessage,
        excludeEmptyString: true
      })
      .max(30, lastNameLengthMessage)
      .test('anon_ack_ln', lastNameMessage, function(value) {
        const { anonymous_acknowledgement } = this.parent
        if (anonymous_acknowledgement && anonymous_acknowledgement[0] === 'Yes')
          return true
        return value != null
      }),
    complainant_home_phone: yup
      .string()
      .matches(phoneRegex, {
        message: homePhoneMessage,
        excludeEmptyString: true
      })
      .test('phone_contact_required', homePhoneMessage, function(value) {
        const {
          anonymous_acknowledgement,
          complainant_cell_phone,
          complainant_address,
          complainant_personal_email,
          complainant_work_email,
          complainant_work_phone
        } = this.parent
        if (
          anonymous_acknowledgement &&
          anonymous_acknowledgement[0] === 'Yes'
        ) {
          return true
        }

        return validContact({
          home_phone: value,
          cell_phone: complainant_cell_phone,
          work_phone: complainant_work_phone,
          email: complainant_personal_email,
          work_email: complainant_work_email,
          address: complainant_address.line1Value,
          city: complainant_address.cityValue,
          state: complainant_address.stateValue,
          zip: complainant_address.zipValue
        })
      }),
    complainant_cell_phone: yup
      .string()
      .matches(phoneRegex, {
        message: cellPhoneMessage,
        excludeEmptyString: true
      })
      .test('phone_contact_required', cellPhoneMessage, function(value) {
        const {
          anonymous_acknowledgement,
          complainant_address,
          complainant_personal_email,
          complainant_home_phone,
          complainant_work_email,
          complainant_work_phone
        } = this.parent
        if (
          anonymous_acknowledgement &&
          anonymous_acknowledgement[0] === 'Yes'
        ) {
          return true
        }

        return validContact({
          home_phone: complainant_home_phone,
          cell_phone: value,
          work_phone: complainant_work_phone,
          email: complainant_personal_email,
          work_email: complainant_work_email,
          address: complainant_address.line1Value,
          city: complainant_address.cityValue,
          state: complainant_address.stateValue,
          zip: complainant_address.zipValue
        })
      }),
    complainant_work_phone: yup
      .string()
      .matches(phoneRegex, {
        message: workPhoneMessage,
        excludeEmptyString: true
      })
      .test('phone_contact_required', workPhoneMessage, function(value) {
        const {
          anonymous_acknowledgement,
          complainant_cell_phone,
          complainant_address,
          complainant_personal_email,
          complainant_home_phone,
          complainant_work_email
        } = this.parent
        if (
          anonymous_acknowledgement &&
          anonymous_acknowledgement[0] === 'Yes'
        ) {
          return true
        }

        return validContact({
          home_phone: complainant_home_phone,
          cell_phone: complainant_cell_phone,
          work_phone: value,
          email: complainant_personal_email,
          work_email: complainant_work_email,
          address: complainant_address.line1Value,
          city: complainant_address.cityValue,
          state: complainant_address.stateValue,
          zip: complainant_address.zipValue
        })
      }),
    complainant_personal_email: yup
      .string()
      .email(personalEmailMessage)
      .max(250, personalEmailLengthMessage)
      .test('email_contact_required', personalEmailMessage, function(value) {
        const {
          anonymous_acknowledgement,
          complainant_address,
          complainant_cell_phone,
          complainant_home_phone,
          complainant_work_email,
          complainant_work_phone
        } = this.parent
        if (
          anonymous_acknowledgement &&
          anonymous_acknowledgement[0] === 'Yes'
        ) {
          return true
        }
        return validContact({
          email: value,
          home_phone: complainant_home_phone,
          cell_phone: complainant_cell_phone,
          work_phone: complainant_work_phone,
          work_email: complainant_work_email,
          address: complainant_address.line1Value,
          city: complainant_address.cityValue,
          state: complainant_address.stateValue,
          zip: complainant_address.zipValue
        })
      }),
    complainant_work_email: yup
      .string()
      .email(workEmailMessage)
      .max(250, workEmailLengthMessage)
      .test('email_contact_required', workEmailMessage, function(value) {
        const {
          anonymous_acknowledgement,
          complainant_address,
          complainant_cell_phone,
          complainant_personal_email,
          complainant_home_phone,
          complainant_work_phone
        } = this.parent
        if (
          anonymous_acknowledgement &&
          anonymous_acknowledgement[0] === 'Yes'
        ) {
          return true
        }
        return validContact({
          work_email: value,
          email: complainant_personal_email,
          home_phone: complainant_home_phone,
          cell_phone: complainant_cell_phone,
          work_phone: complainant_work_phone,
          address: complainant_address.line1Value,
          city: complainant_address.cityValue,
          state: complainant_address.stateValue,
          zip: complainant_address.zipValue
        })
      }),
    complainant_address: yup.object().shape({
      line1Value: yup.string().max(50, addressLengthMessage),
      line2Value: yup.string().max(50, addressLengthMessage),
      cityValue: yup.string().max(30, cityLengthMessage),
      zipValue: yup
        .string()
        .max(10, zipLengthMessage)
        .test('zipcode_required', zipMessage, function(value) {
          const { countryValue } = this.parent
          return countryValue === 'US'
            ? value === '' || zipRegex.test(value)
            : true
        })
    })
  }),
  fields: [
    {
      type: 'content',
      content: ContactDetailsName,
      name: 'name_content'
    },
    {
      type: 'text',
      label: firstNameLabel,
      name: 'complainant_first_name'
    },
    {
      type: 'text',
      label: middleNameLabel,
      name: 'complainant_middle_name',
      requirementLabel: optionalLabel
    },
    {
      type: 'text',
      label: lastNameLabel,
      name: 'complainant_last_name'
    },
    {
      type: 'select',
      label: suffixLabel,
      name: 'complainant_suffix',
      choices: [{ label: '', value: '' }, ...suffixes()],

      requirementLabel: optionalLabel,
      size: 'medium'
    },
    {
      type: 'content',
      content: Divider,
      name: 'divider1'
    },
    {
      type: 'content',
      content: ContactDetailsInfo,
      name: 'contact_info'
    },
    {
      type: 'text',
      label: homePhoneLabel,
      name: 'complainant_home_phone',
      size: 'medium'
    },
    {
      type: 'text',
      label: cellPhoneLabel,
      name: 'complainant_cell_phone',
      size: 'medium'
    },
    {
      type: 'text',
      label: workPhoneLabel,
      name: 'complainant_work_phone',
      size: 'medium'
    },
    {
      type: 'text',
      label: personalEmailLabel,
      name: 'complainant_personal_email',
      size: 'medium'
    },
    {
      type: 'text',
      label: workEmailLabel,
      name: 'complainant_work_email',
      size: 'medium'
    },
    {
      type: 'content',
      content: Divider,
      name: 'divider2'
    },
    {
      type: 'content',
      content: HomeAddressHeading,
      name: 'mailing_address'
    },
    {
      type: 'address',
      name: 'complainant_address',
      label: '',
      hint: '',
      line1Label: addressLabel,
      line2Label: address2Label,
      countryValue: 'US'
    },
    {
      type: 'content',
      content: Divider,
      name: 'divider3'
    },
    {
      type: 'checkbox',
      label: '',
      name: 'anonymous_acknowledgement',
      choices: [
        {
          label: `I wish to remain anonymous. I understand this request will preclude me from having my whistleblower reprisal complaint investigated and may hinder HHS-OIG’s ability to thoroughly review and/or resolve any other allegations in my complaint. I also understand that the information I provide anonymously may be referred to another agency if HHS-OIG determines that action by another agency is warranted under the circumstances. There may be instances where for public safety, crime prevention, or other lawful purpose, HHS-OIG may need to trace your identity.`,
          value: 'Yes'
        }
      ]
    }
  ],
  action: {
    type: 'goto',
    pageId: values => {
      return values.anonymous_acknowledgement &&
        values.anonymous_acknowledgement[0] === 'Yes'
        ? values.complainant_first_name ||
          values.complainant_middle_name ||
          values.complainant_last_name ||
          values.complainant_home_phone ||
          values.complainant_cell_phone ||
          values.complainant_work_phone ||
          values.complainant_personal_email ||
          values.complainant_address.line1Value ||
          values.complainant_address.cityValue ||
          values.complainant_address.stateValue ||
          values.complainant_address.zipValue
          ? 'permission_to_disclose_page'
          : 'review_page'
        : 'non_anon_permission_to_disclose_page'
    }
  }
})
